import { Injectable, signal, computed, effect, PLATFORM_ID, inject, untracked } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { hasCustomSurface, resolveTenantTheme, TENANT_THEME_VARS } from '../theme/tenant-theme.resolve';
import { TenantThemeStorage } from '../theme/tenant-theme.storage';
import { TenantTheme } from '../theme/tenant-theme.types';
import { TenantService } from './tenant.service';

export type Theme = 'light' | 'dark';

export interface TenantThemePreview {
  tenantId: string;
  theme: TenantTheme | null;
}

const STORAGE_KEY = 'skaler-theme';
const USER_CHOICE_KEY = 'skaler-theme:userChoice';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly tenantService = inject(TenantService);
  private readonly tenantThemeStorage = inject(TenantThemeStorage);

  theme = signal<Theme>(this.getInitialTheme());
  isDark = computed(() => this.theme() === 'dark');

  readonly previewTheme = signal<TenantThemePreview | null>(null);

  private readonly userChoice = signal<Theme | null>(this.readUserChoice());

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    effect(() => {
      const current = this.theme();
      localStorage.setItem(STORAGE_KEY, current);
      document.documentElement.classList.toggle('dark', current === 'dark');
      document.documentElement.setAttribute('data-theme', current);
    });

    effect(() => {
      const tenantId = this.tenantService.tenantKey();
      this.tenantThemeStorage.revision();
      if (!tenantId || this.userChoice()) return;

      const mode = this.tenantThemeStorage.get(tenantId)?.mode ?? systemTheme();
      untracked(() => this.theme.set(mode));
    });

    effect(() => {
      const tenantId = this.tenantService.tenantKey();
      const mode = this.theme();
      const preview = this.previewTheme();
      this.tenantThemeStorage.revision();
      if (!tenantId) return;

      const isPreviewing = preview?.tenantId === tenantId;
      const tenantTheme = isPreviewing ? preview.theme : this.tenantThemeStorage.get(tenantId);
      const applied = tenantTheme ? resolveTenantTheme(tenantTheme) : null;

      const root = document.documentElement;
      const vars = applied?.[mode] ?? {};
      for (const name of TENANT_THEME_VARS) {
        const value = vars[name];
        if (value) root.style.setProperty(name, value);
        else root.style.removeProperty(name);
      }
      root.toggleAttribute('data-tenant-surface', hasCustomSurface(vars));

      if (!isPreviewing) this.tenantThemeStorage.setLastApplied(applied);
    });
  }

  toggle() {
    this.setTheme(this.theme() === 'light' ? 'dark' : 'light');
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
    this.userChoice.set(theme);
    try {
      localStorage.setItem(USER_CHOICE_KEY, theme);
    } catch {}
  }

  private getInitialTheme(): Theme {
    if (!isPlatformBrowser(this.platformId)) return 'light';
    const stored = this.readUserChoice() ?? localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return systemTheme();
  }

  private readUserChoice(): Theme | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      const choice = localStorage.getItem(USER_CHOICE_KEY);
      return choice === 'dark' || choice === 'light' ? choice : null;
    } catch {
      return null;
    }
  }
}

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
